import datas from './_datas';
import { nullMultiply, toFloat } from '../helpers/math_helper';

const compute = {
  hot_water_needs: (inhabitants) => {
    return inhabitants * datas.hot_water_conso_per_person;
  },
  energy_cost: (values, energyType, greenEnergy=false) => {
    let price          = 0;
    let priceVariation = 0;

    if (energyType.identifier == 'electricity' && greenEnergy) {
      price          = values.energyPrice;
      priceVariation = values.energyPriceVariation;
    } else {
      price          = energyType.price;
      priceVariation = energyType.price_variation;
    }

    return {
      greenEnergy          : greenEnergy,
      energyType           : energyType,
      energyPrice          : price,
      energyPriceVariation : priceVariation,
    };
  },
  solar_production: (solarPanelSurface, solarSystemRatio, needs, solarMaxProdCovering) => {
    return Math.min(solarPanelSurface * solarSystemRatio, needs * solarMaxProdCovering);
  },
  solar_productions: (solarSystemId, solarPanelSurface, heatingNeeds, hotWaterNeeds) => {
    let heatingSolarProd  = 0;
    let hotWaterSolarProd = 0;

    if (solarSystemId != '') {
      const solarSystem = datas.requests.get_solar_system(solarSystemId);
      hotWaterSolarProd = compute.solar_production(solarPanelSurface, solarSystem.hot_water_ratio, hotWaterNeeds, datas.solar_max_prod_covering.hot_water);
      heatingSolarProd  = (solarSystemId == 2) // SSC
        ? Math.min(solarPanelSurface * solarSystem.heating_ratio - hotWaterSolarProd, heatingNeeds * datas.solar_max_prod_covering.heating)
        : compute.solar_production(solarPanelSurface, solarSystem.heating_ratio, heatingNeeds, datas.solar_max_prod_covering.heating);
    }

    return {
      heating  : heatingSolarProd,
      hotWater : hotWaterSolarProd,
    };
  },
  yearly_solar_maintenance: (heatingSystemId, hotWaterSystemId, solarSystemId) => {
    const heatingSystemHydro  = datas.requests.get_heating_system(heatingSystemId).hydro;
    const hotWaterSystemHydro = (hotWaterSystemId == 0 || hotWaterSystemId == heatingSystemId)
      ? heatingSystemHydro
      : datas.requests.get_hot_water_system(hotWaterSystemId).hydro;

    let yearlySolarMaintenance = 0;
    if (solarSystemId != '') {
      yearlySolarMaintenance = (heatingSystemHydro || hotWaterSystemHydro )
        ? datas.solar_maintenance.with_hydro
        : datas.solar_maintenance.without_hydro;
    }
    return yearlySolarMaintenance;
  },
  solar_total_investment: (solarSystemId, solarPanelSurface, solarEstimatedInvestment) => {
    return (solarSystemId == '')
      ? 0
      : solarPanelSurface * solarEstimatedInvestment;
  },
  yearly_heating_solar_saving: (yearlySolarProd, heatingSystem, energyCost) => {
    let yearlySolarSaving = Math.max((yearlySolarProd / heatingSystem.efficiency / heatingSystem.secondary_efficiency * energyCost) / 100, 0);
    return yearlySolarSaving;
  },
  yearly_hot_water_solar_saving: (yearlySolarProd, hotWaterSystem, energyCost) => {
    let yearlySolarSaving = Math.max((yearlySolarProd / hotWaterSystem.efficiency * energyCost) / 100, 0);
    return yearlySolarSaving;
  },
  yearly_energy_product: (needs, yearlySolarProd) => {
    return Math.max(needs - yearlySolarProd, 0);
  },
  yearly_final_energy_consumption: (yearlyEnergyProd, efficiency) => {
    return Math.max(yearlyEnergyProd / efficiency, 0);
  },
  yearly_energy_cost: (yearlyFinalEnergyConsumption, energyCost) => {
    return Math.max(yearlyFinalEnergyConsumption * energyCost / 100, 0);
  },
  energy_price_variations: (scenario, energyPriceVariation) => {
    let energyPriceVariations = [];
    if (scenario == 'crysis')
      energyPriceVariation = nullMultiply(energyPriceVariation, datas.crysis_energy_ratio);
    for (let i=0; i <= datas.simulation_period-1; i++) {
      energyPriceVariations.push( Math.pow(1 + energyPriceVariation, i) );
    }
    return energyPriceVariations;
  },
  yearly_price_variations: (scenario, energyType) => {
    return datas.energies_yearly_price_variations.map( price => {
      let energyPrice = price[energyType];
      return (scenario == 'crysis')
        ? nullMultiply(energyPrice, 1.3)
        : energyPrice;
    });
  },
  period_energy_cost: (yearlyEnergyCost, energyPriceVariations) => {
    return yearlyEnergyCost * energyPriceVariations.reduce( (sum, energyPriceVariation) => { return sum + energyPriceVariation } );
  },
  period_solar_saving: (solarSystemId, yearlySolarSaving, energyPriceVariations) => {
    if (solarSystemId == '') return 0;
    return yearlySolarSaving * energyPriceVariations.reduce( (sum, energyPriceVariation) => { return sum + energyPriceVariation } );
  },
  enr_prod_ratio: (heatingEnrProd, hotWaterEnrProd, solarTotalProd, totalNeeds) => {
    return (heatingEnrProd + hotWaterEnrProd + solarTotalProd) / totalNeeds;
  },
  yearly_wood_mass: (yearlyFinalEnergyConsumption, energyType) => {
    return (energyType.pci_ratio)
      ? yearlyFinalEnergyConsumption / energyType.pci_ratio
      : 0;
  },
  yearly_wood_volume: (woodVolume, energyType) => {
    return (energyType.density_ratio)
      ? woodVolume / energyType.density_ratio
      : 0;
  },
  estimations_from: {
    invoices: values => {
      const currentHeatingSystem = datas.requests.get_heating_system(values.currentHeatingSystemId);
      const energyType           = datas.requests.get_energy_type_by_id(currentHeatingSystem.energy_type_id);

      const estimatedHeatingConsumption = values.invoicesAmount / energyType.price *100;
      let heatingNeeds                  = estimatedHeatingConsumption * currentHeatingSystem.efficiency;
      const hotWaterNeeds               = compute.hot_water_needs(values.inhabitants);
      if (values.currentHotWaterSystemId == 0 || currentHeatingSystem.id == values.currentHotWaterSystemId)
        heatingNeeds -= hotWaterNeeds;

      return {
        heatingNeeds  : Math.max(heatingNeeds, 0),
        hotWaterNeeds : hotWaterNeeds,
      };
    },
    building_years: values => {
      const heatingRatio                = datas.requests.get_building_year(values.buildingYearsId).heating_ratio;

      const heatingConsumptionEstimated = values.livingArea * heatingRatio;
      const heatingNeeds                = heatingConsumptionEstimated * datas.current_heating_efficiency;
      const hotWaterNeeds               = compute.hot_water_needs(values.inhabitants);

      return {
        heatingNeeds  : heatingNeeds,
        hotWaterNeeds : hotWaterNeeds,
      };
    },
    consumption: values => {
      const currentHeatingSystem        = datas.requests.get_heating_system(values.currentHeatingSystemId);
      const energyVector                = datas.requests.get_energy_vector(values.energyVectorId);

      const heatingConsumptionEstimated = values.consumption * energyVector.pci;
      let heatingNeeds                  = heatingConsumptionEstimated * currentHeatingSystem.efficiency;
      const hotWaterNeeds               = compute.hot_water_needs(values.inhabitants);
      if (values.currentHotWaterSystemId == 0 || currentHeatingSystem.id == values.currentHotWaterSystemId)
        heatingNeeds -= hotWaterNeeds;

      return {
        heatingNeeds  : Math.max(heatingNeeds, 0),
        hotWaterNeeds : hotWaterNeeds,
      };
    },
  },
  solution: {
    energies: (values) => {
      if (values.heating.systemId != 0) {
        let heatingEnergyConsumption  = 0;
        let hotWaterEnergyConsumption = 0;
        let heatingEnergyInfos        = null;
        let hotWaterEnergyInfos       = null;

        const heatingSystem        = datas.requests.get_heating_system(values.heating.systemId);
        const heatingEnergyType    = datas.requests.get_energy_type_by_id(heatingSystem.energy_type_id);

        const solarEnergyProd      = compute.solar_productions(values.solar.systemId, values.solar.panelSurface, values.heating.needs, values.hotWater.needs);

        const heatingEfficiency  = values.heating.efficiency * heatingSystem.secondary_efficiency;
        const heatingEnergyProd  = compute.yearly_energy_product(values.heating.needs, solarEnergyProd.heating);
        heatingEnergyConsumption = compute.yearly_final_energy_consumption(heatingEnergyProd, heatingEfficiency);

        if (values.hotWater.needs > 0 && !['', '-1'].includes(values.hotWater.systemId)) {
          const hotWaterSystem     = datas.requests.get_hot_water_system(values.hotWater.systemId);
          const hotWaterEnergyType = (values.hotWater.systemId == 0)
            ? heatingEnergyType
            : datas.requests.get_energy_type_by_id(hotWaterSystem.energy_type_id);

          const hotWaterEnergyProd  = compute.yearly_energy_product(values.hotWater.needs, solarEnergyProd.hotWater);
          hotWaterEnergyConsumption = compute.yearly_final_energy_consumption(hotWaterEnergyProd, values.hotWater.efficiency);

          if (values.hotWater.systemId == 0
              || heatingEnergyType.id == hotWaterEnergyType.id) { // same energyType as heating System
            const totalEnergyConsumption = heatingEnergyConsumption + hotWaterEnergyConsumption;
            heatingEnergyInfos  = compute.energy_cost(values.heating, heatingEnergyType, values.heating.greenEnergy);
            hotWaterEnergyInfos = compute.energy_cost(values.heating, heatingEnergyType, values.heating.greenEnergy);
          } else {
            heatingEnergyInfos  = compute.energy_cost(values.heating, heatingEnergyType, values.heating.greenEnergy);
            hotWaterEnergyInfos = compute.energy_cost(values.hotWater, hotWaterEnergyType, values.hotWater.greenEnergy);
          }
          hotWaterEnergyInfos.energyProd        = hotWaterEnergyProd;
          hotWaterEnergyInfos.energyConsumption = hotWaterEnergyConsumption;
        } else
          heatingEnergyInfos = compute.energy_cost(values.heating, heatingEnergyType, values.heating.greenEnergy);

        heatingEnergyInfos.energyProd         = heatingEnergyProd;
        heatingEnergyInfos.energyConsumption  = heatingEnergyConsumption;

        return {
          heating  : (heatingEnergyInfos) ? heatingEnergyInfos : {},
          hotWater : (hotWaterEnergyInfos) ? hotWaterEnergyInfos : {},
          solar: {
            energyProd: solarEnergyProd,
          },
        };
      } // no data to update because all select fields are not defined
    },
    heating: (scenario, values, energies) => {
      const heatingSystem  = datas.requests.get_heating_system(values.heating.systemId);
      const energyType     = energies.heating.energyType;

      const energyGhgRatio                 = (energyType.identifier == 'electricity' && values.heating.greenEnergy)
        ? datas.green_electricity_ghg_ratio
        : energyType.ghg_ratio;
      const estimatedInvestment            = values.heating.estimatedInvestment;
      const maintenance                    = values.heating.maintenance;
      const energyPrice                    = (scenario == 'custom' && values.heating.energyPrice != toFloat(energies.heating.energyPrice, 2))
        ? values.heating.energyPrice
        : energies.heating.energyPrice;
      const energyPriceVariation           = (scenario == 'custom' && values.heating.energyPriceVariation != toFloat(energies.heating.energyPriceVariation, 4))
        ? values.heating.energyPriceVariation
        : energies.heating.energyPriceVariation;

      const yearlySolarProd                = energies.solar.energyProd.heating;
      const yearlyEnergyProduct            = energies.heating.energyProd;
      const yearlyFinalEnergyConsumption   = energies.heating.energyConsumption;
      const yearlyEnergyCost               = compute.yearly_energy_cost(yearlyFinalEnergyConsumption, energyPrice);
      const yearlySolarSaving              = compute.yearly_heating_solar_saving(yearlySolarProd, heatingSystem, energyPrice);

      const priceVariations                = compute.energy_price_variations(scenario, energyPriceVariation);

      const yearlyPrimaryEnergyConsumption = yearlyFinalEnergyConsumption * energyType.primary_energy_ratio;
      const yearlyGhgEmissions             = yearlyFinalEnergyConsumption * energyGhgRatio;
      const yearlyWoodMass                 = compute.yearly_wood_mass(yearlyFinalEnergyConsumption, energyType);
      const yearlyWoodVolume               = compute.yearly_wood_volume(yearlyWoodMass, energyType);

      const periodEnergyCost               = compute.period_energy_cost(yearlyEnergyCost, priceVariations);
      const periodSolarSaving              = compute.period_solar_saving(values.solar.systemId, yearlySolarSaving, priceVariations);
      const periodMaintenance              = maintenance * datas.simulation_period;
      const periodTotalCost                = estimatedInvestment + periodEnergyCost + periodMaintenance;

      const avgYearlyEnergyCost            = periodEnergyCost / datas.simulation_period;

      return {
        id                             : values.heating.systemId,
        datas                          : heatingSystem,
        needs                          : values.heating.needs,
        efficiency                     : values.heating.efficiency,
        energyType                     : energyType,
        estimatedInvestment            : estimatedInvestment,
        energyPrice                    : energyPrice,
        energyPriceVariation           : energyPriceVariation,
        maintenance                    : maintenance,
        yearlyPrimaryEnergyConsumption : yearlyPrimaryEnergyConsumption,
        yearlyGhgEmissions             : yearlyGhgEmissions,
        yearlyEnergyProduct            : yearlyEnergyProduct,
        yearlyFinalEnergyConsumption   : yearlyFinalEnergyConsumption,
        yearlyEnergyCost               : yearlyEnergyCost,
        avgYearlyEnergyCost            : avgYearlyEnergyCost,
        yearlySolarSaving              : yearlySolarSaving,
        yearlyWoodMass                 : yearlyWoodMass,
        yearlyWoodVolume               : yearlyWoodVolume,
        priceVariations                : priceVariations,
        periodEnergyCost               : periodEnergyCost,
        periodSolarSaving              : periodSolarSaving,
        periodMaintenance              : periodMaintenance,
        periodTotalCost                : periodTotalCost,
      };
    },
    hot_water: (scenario, values, energies) => {
      let hotWaterSystem = datas.requests.get_hot_water_system(values.hotWater.systemId);
      if (values.hotWater.systemId == -1) {
        return {
          id                             : values.hotWater.systemId,
          datas                          : hotWaterSystem,
          needs                          : 0,
          energyType                     : '',
          estimatedInvestment            : 0,
          energyPrice                    : 0,
          energyPriceVariation           : 0,
          maintenance                    : 0,
          yearlyPrimaryEnergyConsumption : 0,
          yearlyGhgEmissions             : 0,
          yearlyEnergyProduct            : 0,
          yearlyFinalEnergyConsumption   : 0,
          yearlyEnergyCost               : 0,
          avgYearlyEnergyCost            : 0,
          yearlySolarSaving              : 0,
          yearlyWoodMass                 : 0,
          yearlyWoodVolume               : 0,
          priceVariations                : [],
          periodEnergyCost               : 0,
          periodSolarSaving              : 0,
          periodMaintenance              : 0,
          periodTotalCost                : 0,
        };
      }
      if (values.hotWater.systemId == 0)
        hotWaterSystem    = datas.requests.get_hot_water_system(values.heating.systemId);

      const energyType                     = energies.hotWater.energyType;
      const energyGhgRatio                 = (energyType.identifier == 'electricity' && values.hotWater.greenEnergy)
        ? datas.green_electricity_ghg_ratio
        : energyType.ghg_ratio;
      const estimatedInvestment            = values.hotWater.estimatedInvestment;
      const maintenance                    = values.hotWater.maintenance;
      const energyPrice                    = (scenario == 'custom' && values.hotWater.energyPrice != toFloat(energies.hotWater.energyPrice, 2))
        ? values.hotWater.energyPrice
        : energies.hotWater.energyPrice;
      const energyPriceVariation           = (scenario == 'custom' && values.hotWater.energyPriceVariation != toFloat(energies.hotWater.energyPriceVariation, 4))
        ? values.hotWater.energyPriceVariation
        : energies.hotWater.energyPriceVariation;

      const yearlySolarProd                = energies.solar.energyProd.hotWater;
      const yearlyEnergyProduct            = energies.hotWater.energyProd;
      const yearlyFinalEnergyConsumption   = energies.hotWater.energyConsumption;
      const yearlyEnergyCost               = compute.yearly_energy_cost(yearlyFinalEnergyConsumption, energyPrice);
      const yearlySolarSaving              = compute.yearly_hot_water_solar_saving(yearlySolarProd, hotWaterSystem, energyPrice);

      const priceVariations                = compute.energy_price_variations(scenario, energyPriceVariation);

      const yearlyPrimaryEnergyConsumption = yearlyFinalEnergyConsumption * energyType.primary_energy_ratio;
      const yearlyGhgEmissions             = yearlyFinalEnergyConsumption * energyGhgRatio;
      const yearlyWoodMass                 = compute.yearly_wood_mass(yearlyFinalEnergyConsumption, energyType);
      const yearlyWoodVolume               = compute.yearly_wood_volume(yearlyWoodMass, energyType);

      const periodEnergyCost               = compute.period_energy_cost(yearlyEnergyCost, priceVariations);
      const periodSolarSaving              = compute.period_solar_saving(values.solar.systemId, yearlySolarSaving, priceVariations);
      const periodMaintenance              = maintenance * datas.simulation_period;
      const periodTotalCost                = estimatedInvestment + periodEnergyCost + periodMaintenance;

      const avgYearlyEnergyCost            = periodEnergyCost / datas.simulation_period;

      return {
        id                             : values.hotWater.systemId,
        datas                          : hotWaterSystem,
        needs                          : values.hotWater.needs,
        efficiency                     : values.hotWater.efficiency,
        energyType                     : energyType,
        estimatedInvestment            : estimatedInvestment,
        energyPrice                    : energies.hotWater.energyPrice,
        energyPriceVariation           : energies.hotWater.energyPriceVariation,
        maintenance                    : maintenance,
        yearlyPrimaryEnergyConsumption : yearlyPrimaryEnergyConsumption,
        yearlyGhgEmissions             : yearlyGhgEmissions,
        yearlyEnergyProduct            : yearlyEnergyProduct,
        yearlyFinalEnergyConsumption   : yearlyFinalEnergyConsumption,
        yearlyEnergyCost               : yearlyEnergyCost,
        avgYearlyEnergyCost            : avgYearlyEnergyCost,
        yearlySolarSaving              : yearlySolarSaving,
        yearlyWoodMass                 : yearlyWoodMass,
        yearlyWoodVolume               : yearlyWoodVolume,
        priceVariations                : priceVariations,
        periodEnergyCost               : periodEnergyCost,
        periodSolarSaving              : periodSolarSaving,
        periodMaintenance              : periodMaintenance,
        periodTotalCost                : periodTotalCost,
      };
    },
    solar: (values, energies) => {
      const solarSystem = (values.solar.systemId != '')
        ? datas.requests.get_solar_system(values.solar.systemId)
        : '';

      const estimatedInvestment    = (solarSystem == '') ? 0 : values.solar.estimatedInvestment;

      const yearlySolarMaintenance = compute.yearly_solar_maintenance(values.heating.systemId, values.hotWater.systemId, values.solar.systemId);
      const yearlySolarProd        = energies.solar.energyProd;

      const periodMaintenance      = yearlySolarMaintenance * datas.simulation_period;
      const periodTotalCost        = periodMaintenance + estimatedInvestment;

      return {
        datas                  : solarSystem,
        estimatedInvestment    : estimatedInvestment,
        yearlySolarMaintenance : yearlySolarMaintenance,
        yearlySolarProd        : yearlySolarProd,
        periodMaintenance      : periodMaintenance,
        periodTotalCost        : periodTotalCost,
      }
    },
    enr_prod_ratio: (values, heatingSystem, hotWaterSystem, solarSystem) => {
      // HEATING
      const heatingEnrRatio = (heatingSystem.energyType.identifier == 'electricity' && values.heating.greenEnergy)
        ? 1
        : heatingSystem.datas.enr_ratio;
      const heatingEnrProd  = heatingSystem.yearlyEnergyProduct * heatingEnrRatio;
      // HOT WATER
      let hotWaterEnrProd = 0
      if (hotWaterSystem.needs > 0 && hotWaterSystem.id != -1) {
        const hotWaterEnrRatio = (hotWaterSystem.energyType.identifier == 'electricity' && values.hotWater.greenEnergy)
          ? 1
          : hotWaterSystem.datas.enr_ratio;
        hotWaterEnrProd = hotWaterSystem.yearlyEnergyProduct * hotWaterEnrRatio;
      }
      // SOLAR
      const solarTotalProd  = solarSystem.yearlySolarProd.heating + solarSystem.yearlySolarProd.hotWater;

      const totalNeeds      = heatingSystem.needs + hotWaterSystem.needs;

      return compute.enr_prod_ratio(heatingEnrProd, hotWaterEnrProd, solarTotalProd, totalNeeds);
    },
    period_total_cost: values => {
      const yearlyEnergyCost             = values.heatingSystem.yearlyEnergyCost + values.hotWaterSystem.yearlyEnergyCost;
      const yearlyFinalEnergyConsumption = values.heatingSystem.yearlyFinalEnergyConsumption + values.hotWaterSystem.yearlyFinalEnergyConsumption;

      const periodEnergyCost             = values.heatingSystem.periodEnergyCost + values.hotWaterSystem.periodEnergyCost;
      const periodMaintenance            = values.heatingSystem.periodMaintenance + values.hotWaterSystem.periodMaintenance + values.solarSystem.periodMaintenance;
      const totalInvestment              = values.heatingSystem.estimatedInvestment + values.hotWaterSystem.estimatedInvestment + values.solarSystem.estimatedInvestment;
      const periodTotalCost              = values.heatingSystem.periodTotalCost + values.hotWaterSystem.periodTotalCost + values.solarSystem.periodTotalCost;
      const periodSolarSaving            = values.heatingSystem.periodSolarSaving + values.hotWaterSystem.periodSolarSaving;

      const yearlyTotalCost              = periodTotalCost / datas.simulation_period;

      const yearlyWoodMass               = values.heatingSystem.yearlyWoodMass + values.hotWaterSystem.yearlyWoodMass;
      const yearlyWoodVolume             = values.heatingSystem.yearlyWoodVolume + values.hotWaterSystem.yearlyWoodVolume;

      return {
        label                        : values.label,
        yearlyEnergyCost             : yearlyEnergyCost,
        yearlyFinalEnergyConsumption : yearlyFinalEnergyConsumption,
        periodEnergyCost             : periodEnergyCost,
        periodMaintenance            : periodMaintenance,
        totalInvestment              : totalInvestment,
        periodTotalCost              : periodTotalCost,
        yearlyTotalCost              : yearlyTotalCost,
        periodSolarSaving            : periodSolarSaving,
        yearlyWoodMass               : yearlyWoodMass,
        yearlyWoodVolume             : yearlyWoodVolume,
        enrProdRatio                 : values.enrProdRatio,
      };
    },
    period_cost_evolution: values => {
      let priceEvolutionsSerie = [];
      const today              = new Date;

      for (let i=0; i <= (datas.simulation_period - 1); i++) {
        let cost = (i == 0)
          ? values.heatingSystem.estimatedInvestment + values.hotWaterSystem.estimatedInvestment + values.solarSystem.estimatedInvestment
          : priceEvolutionsSerie[i-1];

        cost += (values.heatingSystem.yearlyEnergyCost * values.heatingSystem.priceVariations[i])
                + values.heatingSystem.maintenance;

        if (values.hotWaterSystem.id != -1) {
          cost += values.hotWaterSystem.maintenance + (values.hotWaterSystem.yearlyEnergyCost * values.hotWaterSystem.priceVariations[i]);
        }

        if (values.solarSystem.id != -1)
          cost += values.solarSystem.yearlySolarMaintenance;

        priceEvolutionsSerie.push(cost);
      };

      return {
        label                : values.label,
        priceEvolutionsSerie : priceEvolutionsSerie,
        enrProdRatio         : values.enrProdRatio,
      };
    },
    environmental_impact: (values) => {
      let yearlyPrimaryEnergy  = values.heatingSystem.yearlyPrimaryEnergyConsumption;
      let yearlyGreenHouseGaz  = values.heatingSystem.yearlyGhgEmissions;
      if (values.hotWaterSystem.id != -1) {
        yearlyPrimaryEnergy   += values.hotWaterSystem.yearlyPrimaryEnergyConsumption;
        yearlyGreenHouseGaz   += values.hotWaterSystem.yearlyGhgEmissions;
      }
      const yearlyGhgByCar     = yearlyGreenHouseGaz * datas.ghg_by_car_ratio;

      return {
        label               : values.label,
        yearlyPrimaryEnergy : yearlyPrimaryEnergy,
        yearlyGreenHouseGaz : yearlyGreenHouseGaz,
        yearlyGhgByCar      : yearlyGhgByCar,
      }
    },
    energies_price_variations_before: scenario => {
      return datas.energy_types.map( energy => {
        const energyType             = energy.identifier;
        const priceVariations        = compute.yearly_price_variations(scenario, energyType);

        if (!datas.energies_for_price_variations_chart.includes(energyType))
          return false; // skip energy to avoid displaying it in the chart

        return {
          identifier               : energyType,
          priceVariations          : priceVariations,
          meanYearlyPriceVariation : energy.price_variation * 100,
        };
      }).filter(element => { if (element) return element; });
    },
  },
};

export default compute;



// WEBPACK FOOTER //
// ./assets/javascripts/datas/_compute_rules.js